import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { notifier } from 'components/Toaster';
import { useQueryClient } from 'react-query';
import { AppButton } from 'components';
import useLessonFeedbackStatus from 'hooks/API/POST/useLessonFeedbackStatus';
import {
  DetailPageShell,
  DetailSection,
  DetailTable,
  MissingDetailState,
  formatDateTime,
} from 'pages/lesson-report-details/DetailLayout';

function FeedbackDetail() {
  const { state } = useLocation();
  const history = useHistory();
  const queryClient = useQueryClient();

  const feedback = state?.feedback;

  const { mutate: markReviewed } = useLessonFeedbackStatus();

  if (!feedback) {
    return (
      <MissingDetailState
        links={[{ label: 'Lesson Feedback', to: '/manage/lesson-feedback' }]}
        current="Feedback Details"
        message="Feedback details not found. Please go back and select a feedback entry."
        onBack={() => history.push('/manage/lesson-feedback')}
        backLabel="Back to Feedback"
      />
    );
  }

  const handleMarkReviewed = () => {
    markReviewed(
      { feedbackId: feedback._id, status: 'reviewed' },
      {
        onSuccess: () => {
          notifier.success('Feedback marked as reviewed');
          queryClient.invalidateQueries('lesson-feedback');
          history.push('/manage/lesson-feedback');
        },
        onError: () => notifier.error('Failed to update status'),
      }
    );
  };

  return (
    <DetailPageShell
      links={[{ label: 'Lesson Feedback', to: '/manage/lesson-feedback' }]}
      current="Feedback Details"
      title="Lesson Feedback"
      status={feedback.status === 'new' ? 'New' : 'Reviewed'}
      statusClassName={`badge badge-${feedback.status === 'new' ? 'primary' : 'secondary'}`}
      subtitle={`Submitted on ${formatDateTime(feedback.createdAt)}`}
      onBack={() => history.push('/manage/lesson-feedback')}
      actions={
        feedback.status === 'new' ? (
          <AppButton variantClass="btn btn-success" onClick={handleMarkReviewed}>
            Mark Reviewed
          </AppButton>
        ) : null
      }
    >
      <div className="row">
        <div className="col-lg-8">
          <DetailSection title="Feedback Summary">
            <DetailTable
              rows={[
                { label: 'Student', value: feedback.user?.name || feedback.user?.email || '—' },
                { label: 'Course', value: feedback.course?.courseName || '—' },
                { label: 'Chapter', value: feedback.chapter?.name || '—' },
                { label: 'Lesson', value: feedback.topic?.name || '—' },
                { label: 'Status', value: feedback.status === 'new' ? 'New' : 'Reviewed' },
                { label: 'Submitted At', value: formatDateTime(feedback.createdAt) },
              ]}
            />
          </DetailSection>
        </div>

        <div className="col-lg-4">
          <DetailSection title="Context">
            <DetailTable
              rows={[
                { label: 'Student Email', value: feedback.user?.email || '—' },
                { label: 'Student Name', value: feedback.user?.name || '—' },
                { label: 'Feedback ID', value: feedback._id || '—' },
              ]}
            />
          </DetailSection>
        </div>
      </div>

      <DetailSection title="Feedback Message">
        <DetailTable
          rows={[
            {
              label: 'Submitted Text',
              value: feedback.feedback || 'No feedback text provided.',
              preserveWhitespace: true,
            },
          ]}
        />
      </DetailSection>
    </DetailPageShell>
  );
}

export default FeedbackDetail;
